import React, { useEffect, useState } from 'react';
import { Eye, Send, Loader, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';

const EmailTemplatePreview = ({ backendUrl, authToken }) => {
  const { t } = useTranslation('admin');
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [lang, setLang] = useState('fr');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const authHeaders = {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/admin/mail/email-templates`, { headers: authHeaders });
        const data = await res.json();
        setTemplates(data.templates || []);
        if (data.templates?.length) setSelected(data.templates[0].key);
      } catch (e) {
        logger.error('load templates error', e);
        setFeedback({ type: 'error', msg: t('mail.ms.loadError') });
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doPreview = async (key = selected, language = lang) => {
    if (!key) return;
    setLoadingPreview(true);
    setFeedback(null);
    try {
      const res = await fetch(`${backendUrl}/api/admin/mail/email-templates/preview`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ template_key: key, lang: language })
      });
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      logger.error('preview error', e);
      setFeedback({ type: 'error', msg: t('mail.ms.loadError') });
    } finally {
      setLoadingPreview(false);
    }
  };

  const doSendTest = async () => {
    if (!selected) return;
    setSending(true);
    setFeedback(null);
    try {
      const res = await fetch(`${backendUrl}/api/admin/mail/email-templates/send-test`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ template_key: selected, lang })
      });
      const data = await res.json();
      if (res.ok) {
        setFeedback({ type: 'success', msg: t('mail.ms.testSent', { email: data.sent_to }) });
      } else {
        setFeedback({ type: 'error', msg: data.detail || t('mail.ms.testError') });
      }
    } catch (e) {
      logger.error('send test error', e);
      setFeedback({ type: 'error', msg: t('mail.ms.testError') });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-templates-section">
      <div>
        <h3 className="text-lg font-bold text-slate-900 flex items-center">
          <Mail className="mr-2 text-indigo-600" size={24} />
          {t('mail.ms.templatesTitle')}
        </h3>
        <p className="text-slate-600 mt-1 text-sm">{t('mail.ms.templatesSubtitle')}</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">{t('mail.ms.selectTemplate')}</label>
            <select
              data-testid="template-select"
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setPreview(null); }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              {templates.map((tpl) => (
                <option key={tpl.key} value={tpl.key}>{tpl.label?.[lang] || tpl.key}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">{t('mail.ms.language')}</label>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
              {['fr', 'en'].map((l) => (
                <button
                  key={l}
                  data-testid={`template-lang-${l}`}
                  onClick={() => { setLang(l); setPreview(null); }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    lang === l ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            data-testid="template-preview-btn"
            onClick={() => doPreview()}
            disabled={loadingPreview || !selected}
            className="px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {loadingPreview ? <Loader size={16} className="animate-spin" /> : <Eye size={16} />}
            <span>{t('mail.ms.previewBtn')}</span>
          </button>
          <button
            data-testid="template-sendtest-btn"
            onClick={doSendTest}
            disabled={sending || !selected}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
            <span>{sending ? t('mail.ms.sending') : t('mail.ms.sendTestBtn')}</span>
          </button>
        </div>

        {feedback && (
          <div
            data-testid="template-feedback"
            className={`rounded-xl p-3 text-sm ${
              feedback.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {feedback.msg}
          </div>
        )}
      </div>

      {preview && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="template-preview">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400 uppercase">{t('mail.ms.previewHint')}</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">
              {t('mail.ms.subjectLabel')} <span className="font-normal">{preview.subject}</span>
            </p>
          </div>
          <iframe
            title="email-preview"
            srcDoc={preview.html}
            className="w-full"
            style={{ height: '600px', border: 'none' }}
            sandbox=""
          />
        </div>
      )}
    </div>
  );
};

export default EmailTemplatePreview;
